import React, { useMemo, useState } from "react";
import "./ContactForm.css";
import { submitContactMessage } from "../../../../api/contactMessagesApi";
import { getApiErrorMessage } from "../../../../api/unwrapApiResponse";
import PhoneCountrySelect from "../../../shared/PhoneCountrySelect/PhoneCountrySelect";
import "../../../shared/PhoneCountrySelect/PhoneCountrySelect.css";
import {
  DEFAULT_PHONE_COUNTRY,
  formatPhoneWithDialCode,
  getCountryByCode,
} from "../../../../data/countryPhonePrefixes";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
  privacy: false,
};

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const selectedCountry = useMemo(
    () => getCountryByCode(phoneCountry),
    [phoneCountry]
  );

  const phonePlaceholder = selectedCountry
    ? `${selectedCountry.dial} (555) 000-0000`
    : "(555) 000-0000";

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }

    if (!form.email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!form.privacy) {
      setError("Please agree to the privacy policy.");
      return;
    }

    setBusy(true);
    try {
      await submitContactMessage({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: formatPhoneWithDialCode(phoneCountry, form.phone),
        message: form.message.trim(),
        agreedToPrivacy: true,
      });
      setSuccess(true);
      setForm(EMPTY_FORM);
      setPhoneCountry(DEFAULT_PHONE_COUNTRY);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="contact-form" id="form">
      <div className="contact-form__container">
        <p className="contact-form__label">Contact us</p>
        <h2>Let&apos;s level up your brand, together</h2>
        <p className="contact-form__subtitle">
          Our friendly team would love to hear from you.
        </p>

        <form className="contact-form__form" onSubmit={handleSubmit} noValidate>
          {success ? (
            <p className="contact-form__status contact-form__status--success" role="status">
              Thank you! Your message has been sent successfully.
            </p>
          ) : null}

          {error ? (
            <p className="contact-form__status contact-form__status--error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="contact-form__row">
            <div className="contact-form__field">
              <label htmlFor="cf-first-name">First name</label>
              <input
                id="cf-first-name"
                name="firstName"
                type="text"
                placeholder="First name"
                autoComplete="given-name"
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
                disabled={busy}
                required
              />
            </div>

            <div className="contact-form__field">
              <label htmlFor="cf-last-name">Last name</label>
              <input
                id="cf-last-name"
                name="lastName"
                type="text"
                placeholder="Last name"
                autoComplete="family-name"
                value={form.lastName}
                onChange={(e) => setField("lastName", e.target.value)}
                disabled={busy}
                required
              />
            </div>
          </div>

          <div className="contact-form__field">
            <label htmlFor="cf-email">Email</label>
            <input
              id="cf-email"
              name="email"
              type="email"
              placeholder="you@mail.com"
              autoComplete="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              disabled={busy}
              required
            />
          </div>

          <div className="contact-form__field">
            <label htmlFor="cf-phone">Phone number</label>
            <div className="contact-form__phone">
              <PhoneCountrySelect
                value={phoneCountry}
                disabled={busy}
                theme="contact"
                id="cf-phone-country"
                onChange={(code) => {
                  setPhoneCountry(code);
                  setError("");
                  setSuccess(false);
                }}
              />
              <input
                id="cf-phone"
                name="phone"
                type="tel"
                className="contact-form__phone-input"
                placeholder={phonePlaceholder}
                autoComplete="tel-national"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="contact-form__field">
            <label htmlFor="cf-message">Message</label>
            <textarea
              id="cf-message"
              name="message"
              rows={6}
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="contact-form__check">
            <input
              id="cf-policy"
              name="policy"
              type="checkbox"
              checked={form.privacy}
              onChange={(e) => setField("privacy", e.target.checked)}
              disabled={busy}
              required
            />
            <label htmlFor="cf-policy">
              You agree to our friendly <u>privacy policy</u>.
            </label>
          </div>

          <button
            type="submit"
            className="buttonclip contact-form__submit"
            disabled={busy}
          >
            {busy ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
